/**
 * The /portal light/dark toggle
 * (violation-remediation/design/portal-redesign.html masthead toggle).
 *
 * src/index.css ships a dormant `.dark` palette for every Claude-handoff
 * token (--paper / --ink* / --teal* / --risk*) and Tailwind runs
 * `darkMode: ["class"]`, so flipping `.dark` on the root re-resolves every
 * utility with zero per-component `dark:` variants.
 *
 * Contract:
 *   1. Stamp BOTH `data-theme="dark|light"` (mockup contract) AND `.dark`
 *      (the Tailwind token hook) on `document.documentElement`.
 *   2. Persist the explicit choice to localStorage.
 *   3. Default to `prefers-color-scheme` with no stored choice.
 *
 * SCOPE GUARD: `activatePortalTheme` runs in PortalLayout and its cleanup
 * REMOVES the class on unmount. Root-level, so body-level Radix portals
 * (Sheet, Popover, Dialog, Toaster) inherit dark, but its LIFETIME is the
 * portal, so the Classic filing app and login, still on hardcoded light
 * surfaces, are never dragged into dark.
 *
 * `useSyncExternalStore` keeps the PortalLayout initializer and the masthead
 * toggle in sync without a context/provider, so the toggle stays usable in a
 * test that mounts <PortalDashboard/> without the layout.
 */
import { useSyncExternalStore } from "react";

export type PortalTheme = "light" | "dark";

const STORAGE_KEY = "cc-portal-theme";

/** Guarded for jsdom, where matchMedia is a stub, and for SSR. */
const prefersDark = (): boolean => {
  try {
    return (
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    );
  } catch {
    return false;
  }
};

const readStored = (): PortalTheme | null => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch {
    return null;
  }
};

/** Stored explicit choice wins, else the OS preference. */
export const resolveInitialTheme = (): PortalTheme =>
  readStored() ?? (prefersDark() ? "dark" : "light");

// --- external store ---------------------------------------------------------

let current: PortalTheme = resolveInitialTheme();
const listeners = new Set<() => void>();
const emit = (): void => listeners.forEach((l) => l());

const applyToRoot = (theme: PortalTheme): void => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.setAttribute("data-theme", theme);
  root.classList.toggle("dark", theme === "dark");
};

const clearRoot = (): void => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.removeAttribute("data-theme");
  root.classList.remove("dark");
};

const persist = (theme: PortalTheme): void => {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* Storage unavailable (private mode / SSR): the theme still applies for
       the session, it just will not persist. */
  }
};

/**
 * PortalLayout mount hook: paint the root, and return a cleanup that strips
 * the theme so leaving /portal restores Classic surfaces to light.
 */
export const activatePortalTheme = (): (() => void) => {
  current = resolveInitialTheme();
  applyToRoot(current);
  emit();
  return clearRoot;
};

/** Set, persist, repaint. Called by the masthead toggle. */
export const setPortalTheme = (theme: PortalTheme): void => {
  current = theme;
  persist(theme);
  applyToRoot(theme);
  emit();
};

export const togglePortalTheme = (): void =>
  setPortalTheme(current === "dark" ? "light" : "dark");

const subscribe = (cb: () => void): (() => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};

const getSnapshot = (): PortalTheme => current;

/** The toggle reads `theme` for its icon and aria-label. */
export const usePortalTheme = (): {
  theme: PortalTheme;
  toggle: () => void;
} => {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { theme, toggle: togglePortalTheme };
};
